import { memo, ReactNode, Suspense, useLayoutEffect } from 'react';
import { Center, Loader, Paper } from '@mantine/core';
import cs from 'clsx';
import { useDocumentTitle } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { LayoutPresets } from '@/app/config/layout';
import { RouteItem } from '@/app/model/route';
import { useLocation, useNavigation } from 'react-router-dom';

interface PrivateRouteProps {
  route: Partial<RouteItem>;
}

const PageWrapper = memo(
  ({ children, route }: { children: ReactNode; route: Partial<RouteItem> }) => {
    const navigation = useNavigation();
    return (
      <Paper
        key={route.to}
        withBorder
        className={cs(
          'grow animate-fade overflow-hidden',
          route?.layout || LayoutPresets.default,
        )}
        shadow='xl'
      >
        {navigation.state === 'loading' ? (
          <Center>
            <Loader />
          </Center>
        ) : (
          <Suspense
            fallback={
              <Center>
                <Loader />
              </Center>
            }
          >
            {children}
          </Suspense>
        )}
      </Paper>
    );
  },
);

export const DynamicRoute = memo(({ route }: PrivateRouteProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const title = route.label ? t(route.label, { ns: 'navigation' }) : 'PAMIR';

  useDocumentTitle(title);

  // Chromium can keep the previous tab title after history.pushState.
  // Re-apply when the path changes, not only when the translated string does.
  useLayoutEffect(() => {
    const next = title.trim();
    if (!next) return;
    document.title = next;
    const frame = requestAnimationFrame(() => {
      document.title = next;
    });
    return () => cancelAnimationFrame(frame);
  }, [title, pathname]);

  const Component = route.component;

  return (
    <PageWrapper route={route} key={route.to}>
      <Component />
    </PageWrapper>
  );
});